import type { Connexion, Utilisateur } from '@sirajoptic/shared';

/**
 * Session applicative côté navigateur.
 *
 * Elle vit hors de React : le client HTTP en a besoin pour signer ses requêtes
 * et il n'est appelé depuis aucun composant. React s'y raccroche ensuite par
 * `useSyncExternalStore`, ce qui garantit qu'une déconnexion se propage à tous
 * les écrans montés sans qu'aucun n'ait à la surveiller.
 *
 * Le jeton est conservé dans `localStorage` : la caisse d'un magasin recharge
 * la page, ouvre un second onglet pour comparer deux dossiers, et ne doit pas
 * ressaisir son mot de passe à chaque fois. Le revers est connu — un script
 * injecté dans la page pourrait le lire. La durée de vie courte (12 h côté
 * serveur) et le fait que le jeton ne soit qu'une clé opaque, révocable à tout
 * moment, bornent la casse.
 */

const CLE = 'sirajoptic.session';

export type SessionLocale = {
  jeton: string;
  expire_at: string;
  utilisateur: Utilisateur;
};

function estSession(v: unknown): v is SessionLocale {
  if (typeof v !== 'object' || v === null) return false;
  const s = v as Partial<SessionLocale>;
  return (
    typeof s.jeton === 'string' &&
    typeof s.expire_at === 'string' &&
    typeof s.utilisateur === 'object' &&
    s.utilisateur !== null &&
    typeof s.utilisateur.email === 'string'
  );
}

/** Lecture défensive : le stockage peut être indisponible, vide ou corrompu. */
function lireStockage(): SessionLocale | null {
  try {
    const brut = window.localStorage.getItem(CLE);
    if (!brut) return null;

    const valeur: unknown = JSON.parse(brut);
    if (!estSession(valeur)) return null;

    // Une session déjà expirée n'a pas à être présentée au serveur.
    if (Date.parse(valeur.expire_at) <= Date.now()) return null;

    return valeur;
  } catch {
    return null;
  }
}

function ecrireStockage(session: SessionLocale | null): void {
  try {
    if (session) window.localStorage.setItem(CLE, JSON.stringify(session));
    else window.localStorage.removeItem(CLE);
  } catch {
    /* Navigation privée ou stockage refusé : la session ne tiendra que le
     * temps de l'onglet, ce qui reste utilisable. */
  }
}

let courante: SessionLocale | null = lireStockage();
const abonnes = new Set<() => void>();
let minuterie: ReturnType<typeof setTimeout> | undefined;

function notifier(): void {
  for (const ecouteur of abonnes) ecouteur();
}

/** Ferme la session à l'instant précis où elle expire, sans attendre un 401. */
function armerExpiration(): void {
  if (minuterie !== undefined) clearTimeout(minuterie);
  minuterie = undefined;
  if (!courante) return;

  const restant = Date.parse(courante.expire_at) - Date.now();
  // `setTimeout` sature au-delà de 2^31 ms ; 12 h reste très en deçà.
  if (Number.isFinite(restant) && restant > 0 && restant < 2_147_483_647) {
    minuterie = setTimeout(() => {
      fermerSession();
    }, restant);
  }
}

armerExpiration();

function definir(session: SessionLocale | null): void {
  courante = session;
  ecrireStockage(session);
  armerExpiration();
  notifier();
}

/** Instantané stable — `useSyncExternalStore` exige la même référence entre deux changements. */
export function instantaneSession(): SessionLocale | null {
  return courante;
}

export function sAbonnerSession(ecouteur: () => void): () => void {
  abonnes.add(ecouteur);
  return () => {
    abonnes.delete(ecouteur);
  };
}

/** Jeton à présenter en `Authorization: Bearer`, s'il y en a un. */
export function jetonCourant(): string | undefined {
  return courante?.jeton;
}

export function ouvrirSession(connexion: Connexion): void {
  definir({
    jeton: connexion.jeton,
    expire_at: connexion.expire_at,
    utilisateur: connexion.utilisateur,
  });
}

export function fermerSession(): void {
  if (courante === null) return;
  definir(null);
}

/* Se déconnecter dans un onglet doit déconnecter les autres : l'événement
 * `storage` n'est émis que dans les onglets qui n'ont pas fait l'écriture. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== CLE) return;
    courante = lireStockage();
    armerExpiration();
    notifier();
  });
}
